import { computed, DestroyRef, inject, Injectable, Injector, Signal, signal, WritableSignal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { ActiveTimerDto, TaskTimerHttpService } from '@app/shared/services/http/task-timer-http.service';
import { ToastService } from '@app/shared/services/shared/toast.service';
import { TasksDataService } from '@app/shared/services/tasks-data.service';
import { TaskTimer } from '@app/shared/types/models/task/task-timer';
import { Task } from '@app/shared/types/models/task/task.model';
import { TranslateService } from '@ngx-translate/core';
import { filter, fromEvent, map, Subject, switchMap, take } from 'rxjs';

type TimerBroadcastMessage =
  | { type: 'started'; timerDto: ActiveTimerDto }
  | { type: 'stopped'; taskId: string; alreadyTrackedSeconds: number }
  | { type: 'cleared' };

interface PendingTaskBind {
  taskSignal: Signal<Task | undefined>;
  timer: TaskTimer;
}

/**
 * TimerService - Owns the active timer, binds it to a task and reconciles API/browser events.
 * Scope: Global (`providedIn: 'root'`).
 * Usage: Active timer bar, task timer controls and time-report dialogs.
 */
@Injectable({ providedIn: 'root' })
export class TimerService {
  private static readonly BROADCAST_CHANNEL_NAME = 'proffeo-task-timer';

  private readonly toastService: ToastService = inject(ToastService);
  private readonly translateService: TranslateService = inject(TranslateService);
  private readonly taskTimerHttpService: TaskTimerHttpService = inject(TaskTimerHttpService);
  private readonly tasksDataService: TasksDataService = inject(TasksDataService);
  private readonly injector: Injector = inject(Injector);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);

  private readonly _timerTask = signal<Signal<Task | undefined> | undefined>(undefined);
  private readonly _activeTimer = signal<TaskTimer | undefined>(undefined);
  private readonly pendingTaskBind$ = new Subject<PendingTaskBind>();
  private readonly broadcastChannel: BroadcastChannel | undefined =
    typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel(TimerService.BROADCAST_CHANNEL_NAME) : undefined;

  // Read the already acquired cache signal; this computed never initiates HTTP.
  public readonly timerTask: Signal<Task> = computed(() => this._timerTask()?.());
  public readonly activeTimer: Signal<TaskTimer> = this._activeTimer.asReadonly();
  public readonly isTimerActive: Signal<boolean> = computed(() => !!this._activeTimer());

  public constructor() {
    this.setupPendingTaskBind();
    this.restoreActiveTimerFromApi();
    this.setupBroadcastChannel();
    this.setupVisibilityReconcile();
    this.destroyRef.onDestroy(() => this.broadcastChannel?.close());
  }

  public initializeTimer(timerDto: ActiveTimerDto, options?: { broadcast?: boolean }): void {
    const timer = this.createTimerFromDto(timerDto);
    this._activeTimer.set(timer);
    this.bindTaskToActiveTimer(timerDto.projectTaskId, timer);

    if (options?.broadcast !== false) {
      this.postBroadcast({ type: 'started', timerDto });
    }
  }

  public startTimer(task: Task, opts?: { files?: File[]; comment?: string; locationId?: string }): void {
    if (!task?.id) return;
    this.taskTimerHttpService
      .start(task.id, { files: opts?.files, comment: opts?.comment, locationId: opts?.locationId })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(dto => {
        this.toastService.success(this.translateService.instant('time-reports-form-modal-timer-started'));

        const startedOn = this.resolveStartedOn(dto?.timerStartedOn);
        const alreadyTrackedSeconds = dto?.alreadyTrackedSeconds ?? task.timer()?.alreadyTrackedSeconds ?? 0;
        const next = new TaskTimer({
          id: dto?.id ?? task.timer()?.id ?? null,
          taskId: task.id,
          userId: dto?.userId,
          alreadyTrackedSeconds,
          timerStartedOn: startedOn
        });
        (task.timer as WritableSignal<TaskTimer | undefined>).set(next);

        const timerDto: ActiveTimerDto = {
          id: dto.id,
          userId: dto.userId,
          projectTaskId: task.id,
          locationId: opts?.locationId ?? '',
          alreadyTrackedSeconds,
          timerStartedOn: startedOn.toISOString()
        };
        this.initializeTimer(timerDto);
      });
  }

  public stopTimer(task: Task, opts?: { files?: File[]; comment?: string }): void {
    if (!task?.id) return;
    this.taskTimerHttpService
      .stop(task.id, { files: opts?.files, comment: opts?.comment })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(resp => {
        const timerDto = resp?.item1;

        const alreadyTrackedSeconds = timerDto.alreadyTrackedSeconds ?? task.timer()?.alreadyTrackedSeconds ?? 0;
        const next = new TaskTimer({
          id: timerDto.id ?? task.timer()?.id ?? null,
          taskId: task.id,
          userId: timerDto.userId,
          alreadyTrackedSeconds,
          timerStartedOn: undefined
        });
        (task.timer as WritableSignal<TaskTimer | undefined>).set(next);
        // Snapshot the stopped timer before fetching recalculated task statistics.
        this.tasksDataService.refreshTask(task.id);
        this.toastService.success(this.translateService.instant('time-reports-form-modal-timer-stopped'));

        this.clearTimer({ broadcast: false });
        this.postBroadcast({
          type: 'stopped',
          taskId: task.id,
          alreadyTrackedSeconds
        });
      });
  }

  public clearTimer(options?: { broadcast?: boolean }): void {
    this._timerTask.set(undefined);
    this._activeTimer.set(undefined);

    if (options?.broadcast !== false) {
      this.postBroadcast({ type: 'cleared' });
    }
  }

  private restoreActiveTimerFromApi(): void {
    this.taskTimerHttpService
      .getActive()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(timerDto => {
        if (timerDto) {
          this.initializeTimer(timerDto, { broadcast: false });
        }
      });
  }

  private setupBroadcastChannel(): void {
    if (!this.broadcastChannel) {
      return;
    }

    this.broadcastChannel.onmessage = (event: MessageEvent<TimerBroadcastMessage>): void => {
      this.handleBroadcastMessage(event.data);
    };
  }

  private setupVisibilityReconcile(): void {
    if (typeof document === 'undefined') {
      return;
    }

    fromEvent(document, 'visibilitychange')
      .pipe(
        filter(() => document.visibilityState === 'visible'),
        switchMap(() => this.taskTimerHttpService.getActive()),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(timerDto => {
        if (timerDto) {
          this.initializeTimer(timerDto, { broadcast: false });
          return;
        }
        if (this._activeTimer()) {
          this.clearTimer({ broadcast: false });
        }
      });
  }

  private handleBroadcastMessage(message: TimerBroadcastMessage): void {
    if (!message?.type) {
      return;
    }

    switch (message.type) {
      case 'started':
        this.initializeTimer(message.timerDto, { broadcast: false });
        break;
      case 'stopped': {
        this.applyStoppedTimerToTask(message.taskId, message.alreadyTrackedSeconds);
        this.clearTimer({ broadcast: false });
        break;
      }
      case 'cleared':
        this.clearTimer({ broadcast: false });
        break;
    }
  }

  private applyStoppedTimerToTask(taskId: string, alreadyTrackedSeconds: number): void {
    const task = this.tasksDataService.getById(taskId)();
    if (!task) {
      return;
    }

    const current = task.timer();
    task.timer.set(
      new TaskTimer({
        id: current?.id,
        taskId,
        userId: current?.userId,
        alreadyTrackedSeconds,
        timerStartedOn: undefined
      })
    );
  }

  private setupPendingTaskBind(): void {
    this.pendingTaskBind$
      .pipe(
        switchMap(({ taskSignal, timer }) => {
          return toObservable(taskSignal, { injector: this.injector }).pipe(
            filter((loadedTask): loadedTask is Task => !!loadedTask),
            take(1),
            map(loadedTask => ({ loadedTask, timer }))
          );
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(({ loadedTask, timer }) => {
        if (this._activeTimer()?.id !== timer.id) {
          return;
        }
        loadedTask.timer.set(this._activeTimer() ?? timer);
      });
  }

  private bindTaskToActiveTimer(taskId: string, timer: TaskTimer): void {
    const taskSignal = this.tasksDataService.getById(taskId);
    this._timerTask.set(taskSignal);
    const task = taskSignal();
    if (task) {
      task.timer.set(timer);
      return;
    }

    this.pendingTaskBind$.next({ taskSignal, timer });
  }

  private createTimerFromDto(timerDto: ActiveTimerDto): TaskTimer {
    return new TaskTimer({
      id: timerDto.id,
      taskId: timerDto.projectTaskId,
      userId: timerDto.userId,
      timerStartedOn: new Date(timerDto.timerStartedOn),
      alreadyTrackedSeconds: timerDto.alreadyTrackedSeconds
    });
  }

  private resolveStartedOn(timerStartedOn: Date | string | undefined): Date {
    if (!timerStartedOn) {
      return new Date();
    }
    return timerStartedOn instanceof Date ? timerStartedOn : new Date(timerStartedOn);
  }

  private postBroadcast(message: TimerBroadcastMessage): void {
    this.broadcastChannel?.postMessage(message);
  }
}
